import React, { useState, useEffect, useMemo } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { 
  collection, 
  query, 
  orderBy, 
  onSnapshot
} from 'firebase/firestore';
import { db } from '../../services/firebase';
import { deleteClientWithCpf, saveClientWithUniqueCpf } from '../../services/clientService';
import { formatCpf, isValidCpf, normalizeCpf } from '../../utils/cpf';
import { groupClientsByIdentity, normalizeEmail, reservationBelongsToClient } from '../../utils/clientIdentity';
import { 
  Plus, 
  Search, 
  Edit2, 
  Trash2, 
  User,
  Mail,
  Phone,
  MapPin,
  IdCard,
  History,
  CalendarDays,
  CheckCircle2,
  CircleDollarSign,
  Clock3,
  ArrowLeft,
  ChevronRight
} from 'lucide-react';
import LoadingState from '../shared/LoadingState';
import './Clients.css';

const reservationStatusLabels = {
  pending: 'Pendente',
  confirmed: 'Confirmada',
  completed: 'Concluída',
  cancelled: 'Cancelada',
  no_show: 'Não compareceu'
};

const paymentStatusLabels = {
  unpaid: 'Pendente',
  partial: 'Parcial',
  paid: 'Pago'
};

const toDate = value => {
  if (!value) return null;
  const parsed = value.toDate ? value.toDate() : new Date(value);
  return Number.isNaN(parsed.getTime()) ? null : parsed;
};

const Clients = () => {
  const { userData } = useAuth();

  const companyId = userData?.role === 'collaborator'
    ? userData?.currentCompanyId
    : userData?.companyId;
  
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [currentClient, setCurrentClient] = useState(null);
  const [historyClient, setHistoryClient] = useState(null);
  const [reservations, setReservations] = useState([]);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    cpf: '',
    address: '',
    city: '',
    state: '',
    zipCode: '',
    notes: ''
  });

  useEffect(() => {
    if (!companyId) return undefined;

    setLoading(true);
    const unsubscribeClients = onSnapshot(
      query(collection(db, `companies/${companyId}/clients`), orderBy('name', 'asc')),
      snapshot => {
        setClients(groupClientsByIdentity(snapshot.docs.map(item => ({ id: item.id, ...item.data() }))));
        setLoading(false);
      },
      error => {
        console.error('Erro ao acompanhar clientes:', error);
        setLoading(false);
      }
    );
    const unsubscribeReservations = onSnapshot(
      query(collection(db, `companies/${companyId}/reservations`), orderBy('date', 'desc')),
      snapshot => setReservations(snapshot.docs.map(item => ({ id: item.id, ...item.data() }))),
      error => console.error('Erro ao acompanhar o histórico dos clientes:', error)
    );

    return () => {
      unsubscribeClients();
      unsubscribeReservations();
    };
  }, [companyId]);

  const filteredClients = useMemo(() => {
    if (!searchTerm) {
      return clients;
    }

    return clients.filter(client =>
      client.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      client.email?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      client.phone?.includes(searchTerm) ||
      normalizeCpf(client.cpf).includes(normalizeCpf(searchTerm))
    );
  }, [clients, searchTerm]);

  const handleOpenModal = (client = null) => {
    if (client) {
      setCurrentClient(client);
      setFormData({
        name: client.name || '',
        email: client.email || '',
        phone: client.phone || '',
        cpf: client.cpf || '',
        address: client.address || '',
        city: client.city || '',
        state: client.state || '',
        zipCode: client.zipCode || '',
        notes: client.notes || ''
      });
    } else {
      setCurrentClient(null);
      setFormData({
        name: '',
        email: '',
        phone: '',
        cpf: '',
        address: '',
        city: '',
        state: '',
        zipCode: '',
        notes: ''
      });
    }
    setShowModal(true);
  };

  const handleCloseModal = () => {
    setShowModal(false);
    setCurrentClient(null);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.name || !formData.phone || !formData.cpf) {
      alert('Nome, telefone e CPF são obrigatórios');
      return;
    }
    if (!isValidCpf(formData.cpf)) {
      alert('Informe um CPF válido');
      return;
    }

    const duplicate = clients.find(client => client.id !== currentClient?.id && (
      normalizeCpf(client.cpfNormalized || client.cpf) === normalizeCpf(formData.cpf) ||
      (normalizeEmail(formData.email) && client.clientEmails?.includes(normalizeEmail(formData.email)))
    ));
    if (duplicate) {
      alert('Este CPF ou e-mail já pertence a um cliente cadastrado. O cadastro existente será exibido.');
      handleCloseModal();
      setHistoryClient(duplicate);
      return;
    }

    try {
      const clientData = {
        ...formData,
        totalReservations: currentClient?.totalReservations || 0,
        totalSpent: currentClient?.totalSpent || 0,
        createdBy: currentClient?.createdBy || userData.email,
        createdByEmail: currentClient?.createdByEmail || userData.email
      };
      await saveClientWithUniqueCpf({
        companyId,
        clientId: currentClient?.id,
        previousCpf: currentClient?.cpfNormalized || currentClient?.cpf,
        data: clientData
      });
      alert(currentClient ? 'Cliente atualizado com sucesso!' : 'Cliente criado com sucesso!');

      handleCloseModal();
    } catch (error) {
      console.error('Erro ao salvar cliente:', error);
      alert(error?.message || 'Erro ao salvar cliente');
    }
  };

  const handleDelete = async (client) => {
    if (!window.confirm('Tem certeza que deseja excluir este cliente?')) {
      return;
    }

    try {
      const records = client.clientRecords || [client];
      for (const record of records) {
        await deleteClientWithCpf({ companyId, client: record });
      }
      alert('Cliente excluído com sucesso!');
    } catch (error) {
      console.error('Erro ao excluir cliente:', error);
      alert('Erro ao excluir cliente');
    }
  };

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL'
    }).format(value || 0);
  };

  const getClientReservations = client => {
    return reservations.filter(reservation => reservationBelongsToClient(reservation, client));
  };

  const formatDate = value => {
    const date = toDate(value);
    return date ? date.toLocaleString('pt-BR', { dateStyle: 'short', timeStyle: 'short' }) : '-';
  };

  const selectedHistory = historyClient ? getClientReservations(historyClient) : [];
  const historySummary = selectedHistory.reduce((summary, reservation) => ({
    total: summary.total + (Number(reservation.totalAmount) || 0),
    paid: summary.paid + (Number(reservation.paidAmount) || 0),
    checkIns: summary.checkIns + (reservation.checkInStatus === 'checked_in' ? 1 : 0)
  }), { total: 0, paid: 0, checkIns: 0 });

  if (loading) {
    return (
      <div className="clients-container">
        <LoadingState title="Carregando clientes" subtitle="Buscando cadastros e historico" />
      </div>
    );
  }

  return (
    <div className="clients-container">
      {!historyClient ? (
        <>
      {/* Header */}
      <div className="clients-header">
        <div>
          <h1>Clientes</h1>
          <p>{filteredClients.length} cliente(s) cadastrado(s)</p>
        </div>
        <button className="btn-primary" onClick={() => handleOpenModal()}>
          <Plus size={20} />
          Novo Cliente
        </button>
      </div>

      {/* Busca */}
      <div className="clients-search">
        <div className="search-box">
          <Search size={20} />
          <input
            type="text"
            placeholder="Buscar por nome, email, telefone ou CPF..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
        </div>
      </div>

      {/* Grid de Clientes */}
      <div className="clients-grid">
        {filteredClients.length === 0 ? (
          <div className="empty-state">
            <User size={48} color="#ccc" />
            <p>Nenhum cliente encontrado</p>
            <button className="btn-secondary" onClick={() => handleOpenModal()}>
              Cadastrar primeiro cliente
            </button>
          </div>
        ) : (
          filteredClients.map((client) => {
            const clientReservations = getClientReservations(client);
            const paidTotal = clientReservations.reduce((sum, reservation) => sum + (Number(reservation.paidAmount) || 0), 0);
            return (
              <div key={client.id} className="client-card">
              <div className="client-card-header">
                <div className="client-avatar">
                  <User size={24} />
                </div>
                <div className="client-info">
                  <h3>{client.name}</h3>
                  <p className="client-stats">
                    {clientReservations.length} reservas · {formatCurrency(paidTotal)} pagos
                  </p>
                  {client.groupedRecords > 1 && (
                    <span className="client-grouped-label">{client.groupedRecords} cadastros agrupados</span>
                  )}
                </div>
              </div>

              <div className="client-card-body">
                {client.email && (
                  <div className="client-detail">
                    <Mail size={16} />
                    <span>{client.email}</span>
                  </div>
                )}
                {client.phone && (
                  <div className="client-detail">
                    <Phone size={16} />
                    <span>{client.phone}</span>
                  </div>
                )}
                {client.cpf && (
                  <div className="client-detail">
                    <IdCard size={16} />
                    <span>{formatCpf(client.cpf)}</span>
                  </div>
                )}
                {client.city && (
                  <div className="client-detail">
                    <MapPin size={16} />
                    <span>{client.city} - {client.state}</span>
                  </div>
                )}
              </div>

              <div className="client-card-actions">
                <button
                  className="client-history-button"
                  onClick={() => setHistoryClient(client)}
                  title="Ver histórico"
                >
                  <History size={18} />
                  Histórico
                </button>
                <button 
                  className="btn-icon" 
                  onClick={() => handleOpenModal(client)}
                  title="Editar"
                >
                  <Edit2 size={18} />
                </button>
                <button 
                  className="btn-icon btn-danger" 
                  onClick={() => handleDelete(client)}
                  title="Excluir"
                >
                  <Trash2 size={18} />
                </button>
              </div>
              </div>
            );
          })
        )}
      </div>
        </>
      ) : (
        <section className="client-history-page" aria-labelledby="client-history-title">
          <nav className="client-history-breadcrumb" aria-label="Navegação do histórico">
            <button type="button" onClick={() => setHistoryClient(null)}>Clientes</button>
            <ChevronRight size={16} aria-hidden="true" />
            <span aria-current="page">{historyClient.name}</span>
          </nav>

          <header className="client-history-header">
            <button
              type="button"
              className="client-history-back"
              onClick={() => setHistoryClient(null)}
              aria-label="Voltar para a lista de clientes"
              title="Voltar para clientes"
            >
              <ArrowLeft size={20} />
            </button>
            <div>
              <h1 id="client-history-title">Histórico de {historyClient.name}</h1>
              <p className="client-history-contact">
                <span>{formatCpf(historyClient.cpf)}</span>
                <span>{historyClient.phone || 'Sem telefone'}</span>
              </p>
            </div>
          </header>

          <div className="client-history-content">
            <div className="client-history-summary">
              <div><CalendarDays size={20} /><span>Reservas</span><strong>{selectedHistory.length}</strong></div>
              <div><CircleDollarSign size={20} /><span>Total reservado</span><strong>{formatCurrency(historySummary.total)}</strong></div>
              <div><CheckCircle2 size={20} /><span>Total pago</span><strong>{formatCurrency(historySummary.paid)}</strong></div>
              <div><Clock3 size={20} /><span>Saldo pendente</span><strong>{formatCurrency(Math.max(0, historySummary.total - historySummary.paid))}</strong></div>
              <div><CheckCircle2 size={20} /><span>Check-ins</span><strong>{historySummary.checkIns}</strong></div>
            </div>

            <div className="client-history-list">
              {selectedHistory.length === 0 ? (
                <div className="client-history-empty">Nenhuma reserva encontrada para este cliente.</div>
              ) : selectedHistory.map(reservation => (
                <div className="client-history-row" key={reservation.id}>
                  <div className="client-history-main">
                    <strong>{reservation.serviceName || 'Serviço'}</strong>
                    <span>{formatDate(reservation.date || reservation.checkInDate)}</span>
                    <small>Reserva {reservation.id.slice(0, 8).toUpperCase()}</small>
                  </div>
                  <div className="client-history-values">
                    <strong>{formatCurrency(reservation.totalAmount)}</strong>
                    <span>Pago: {formatCurrency(reservation.paidAmount)}</span>
                  </div>
                  <div className="client-history-statuses">
                    <span className={`client-status status-${reservation.status || 'pending'}`}>
                      {reservationStatusLabels[reservation.status] || reservation.status || 'Pendente'}
                    </span>
                    <span className={`client-status payment-${reservation.paymentStatus || 'unpaid'}`}>
                      {paymentStatusLabels[reservation.paymentStatus] || reservation.paymentStatus || 'Pendente'}
                    </span>
                    <span className={`client-status checkin-${reservation.checkInStatus || 'pending'}`}>
                      {reservation.checkInStatus === 'checked_in' ? 'Check-in realizado' : reservation.status === 'no_show' ? 'Não compareceu' : 'Check-in pendente'}
                    </span>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Modal de Criação/Edição */}
      {showModal && (
        <div className="modal-overlay" onClick={handleCloseModal}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>{currentClient ? 'Editar Cliente' : 'Novo Cliente'}</h2>
              <button className="btn-close" onClick={handleCloseModal}>×</button>
            </div>

            <form onSubmit={handleSubmit} className="client-form">
              <div className="form-row">
                <div className="form-group">
                  <label>Nome Completo *</label>
                  <input
                    type="text"
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    required
                    placeholder="João da Silva"
                  />
                </div>

                <div className="form-group">
                  <label>Email</label>
                  <input
                    type="email"
                    value={formData.email}
                    onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                    placeholder="joao@email.com"
                  />
                </div>
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label>Telefone *</label>
                  <input
                    type="tel"
                    value={formData.phone}
                    onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                    required
                    placeholder="(11) 98765-4321"
                  />
                </div>

                <div className="form-group">
                  <label>CPF *</label>
                  <input
                    type="text"
                    value={formData.cpf}
                    onChange={(e) => setFormData({ ...formData, cpf: formatCpf(e.target.value) })}
                    placeholder="000.000.000-00"
                    inputMode="numeric"
                    maxLength="14"
                    required
                  />
                </div>
              </div>

              <div className="form-group">
                <label>Endereço</label>
                <input
                  type="text"
                  value={formData.address}
                  onChange={(e) => setFormData({ ...formData, address: e.target.value })}
                  placeholder="Rua, número, complemento"
                />
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label>Cidade</label>
                  <input
                    type="text"
                    value={formData.city}
                    onChange={(e) => setFormData({ ...formData, city: e.target.value })}
                    placeholder="São Paulo"
                  />
                </div>

                <div className="form-group">
                  <label>Estado</label>
                  <input
                    type="text"
                    value={formData.state}
                    onChange={(e) => setFormData({ ...formData, state: e.target.value })}
                    placeholder="SP"
                    maxLength="2"
                  />
                </div>

                <div className="form-group">
                  <label>CEP</label>
                  <input
                    type="text"
                    value={formData.zipCode}
                    onChange={(e) => setFormData({ ...formData, zipCode: e.target.value })}
                    placeholder="00000-000"
                  />
                </div>
              </div>

              <div className="form-group">
                <label>Observações</label>
                <textarea
                  value={formData.notes}
                  onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                  rows="3"
                  placeholder="Observações adicionais sobre o cliente..."
                />
              </div>

              <div className="form-actions">
                <button type="button" className="btn-secondary" onClick={handleCloseModal}>
                  Cancelar
                </button>
                <button type="submit" className="btn-primary">
                  {currentClient ? 'Atualizar' : 'Criar'} Cliente
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default Clients;
